/**
 * session.ts — owns the lifecycle of a single in-game session.
 *
 * Extracted from main.ts during v2M1 when the app gained a main menu.
 * Before v2M1 the app booted directly to gameplay and never tore down;
 * with the main menu + "Return to Main Menu" option, every in-game mount
 * + the render/sim loops need a coordinated teardown path.
 *
 * Lifecycle:
 *   const session = startGameSession(deps)   // mounts HUD, panels, loops
 *   ...                                      // gameplay runs
 *   session.destroy()                        // stops loops, unmounts UI
 *
 * After destroy(), the canvas and the LoadedGameData are still alive —
 * they belong to the outer main.ts shell and are reused across sessions.
 */

import type { LoadedGameData } from '../data/loader';
import type { Command } from '../game/commands';
import { startSimLoop } from '../game/loop';
import { computeViewport, initRenderer, renderFrame } from '../render/canvas';
import { mountHud } from './hud';
import { mountAlertFeed } from './alert-feed';
import { mountDetailPanel } from './detail-panel';
import { mountInGameMenu, type InGameMenuHandle } from './in-game-menu';
import {
  mountEndGameOverlay,
  type EndGameOverlayHandle,
} from './end-game-overlay';
import { mountMiniGalaxy } from './mini-galaxy';
import { mountJpTooltip } from './jp-tooltip';
import { mountResearchPanel } from './research-panel';
import { mountDevToolbox } from './dev-toolbox';
import { mountFleetPanel } from './fleet-panel';
import { attachInputHandlers } from './input';
import { createSpeedController } from '../game/speed-controller';
import { mountSpeedWidget } from './speed-widget';
import { createUiState } from './state';
import type { Settings } from '../settings/types';
import type { GameState } from '../state/types';
import { getAudioEngine } from '../audio';

export interface StartGameSessionDeps {
  canvas: HTMLCanvasElement;
  data: LoadedGameData;
  /**
   * Pre-built GameState. Caller decides whether this is a fresh scenario
   * (createInitialGameState) or a deserialised save (deserializeGameState);
   * the session is agnostic. Decoupled in v2M2 so the Load flow can reuse
   * the same session pipeline as a new game.
   */
  state: GameState;
  /**
   * Called when the player picks "Return to Main Menu" from the in-game
   * menu. Session applies the v2M4 confirmReturnToMainMenu gate before
   * invoking this callback.
   */
  onReturnToMainMenu: () => void;
  /**
   * Called when the player picks "Exit" from the in-game menu. Session
   * applies the v2M4 confirmExit gate before invoking this callback.
   */
  onExit: () => void;
  /**
   * Called when the player picks "Save" from the in-game menu (v2M2).
   * Receives a getter for the current sim state so the caller can
   * serialise it without holding a stale reference. Optional — sessions
   * that don't wire saving (e.g. test harnesses) can omit it.
   */
  onSave?: (getState: () => GameState) => void;
  /**
   * Called when the player picks "Settings" from the in-game menu (v2M4).
   * main.ts mounts the Settings overlay; session does not own it because
   * Settings is also reachable from the main-menu and lives at the app level.
   */
  onSettings?: () => void;
  /**
   * Live settings reference used by the session for:
   *  - seeding the SpeedController's initialSpeed (v2M3 + v2M4),
   *  - gating the Return-to-Main-Menu and Exit confirm prompts (v2M4).
   *
   * The reference is read fresh on each prompt so a settings change
   * mid-session takes effect immediately (no need to re-create the session).
   */
  getSettings?: () => Settings;
}

export interface SessionHandle {
  destroy(): void;
}

export function startGameSession(deps: StartGameSessionDeps): SessionHandle {
  const {
    canvas,
    state,
    onReturnToMainMenu,
    onExit,
    onSave,
    onSettings,
    getSettings,
  } = deps;

  const ui = createUiState();

  const rc = initRenderer(canvas);
  const hud = mountHud(document.body);
  // v2M8.7 — kick off the ambient music bed for this session. Idempotent
  // on the engine side; session destroy fades it out.
  getAudioEngine().startAmbientBed();
  // v2M8.3 — alert feed (UI_UX §1.5 + §7). Player-only viewer scope:
  // F10 enemy-POV doesn't remap which empire's events surface. Click
  // on a toast with a `source` jumps the camera to that target.
  const alertFeed = mountAlertFeed(document.body, {
    onJumpTo: (source) => {
      if (source.kind === 'planet') {
        const planet = state.planets.get(source.id);
        if (planet) {
          ui.focusedSystemId = planet.systemId;
          ui.viewMode = 'system';
        }
      } else if (source.kind === 'ship') {
        const ship = state.ships.get(source.id);
        if (ship && ship.systemId !== null) {
          ui.focusedSystemId = ship.systemId;
          ui.viewMode = 'system';
        }
      } else if (source.kind === 'system') {
        ui.focusedSystemId = source.id;
        ui.viewMode = 'system';
      }
    },
  });
  // Speed widget mount happens after the controller exists (later in the
  // function); reference declared here but assigned after createSpeedController.

  // Sim loop is started below; create a forward reference so both the input
  // handler and the detail panel can queue commands into it
  // (input / UI click → command queue → sim tick).
  let queueCommandRef: ((cmd: Command) => void) | null = null;
  const dispatchCommand = (cmd: Command): void => {
    queueCommandRef?.(cmd);
  };

  const panel = mountDetailPanel(document.body, { queueCommand: dispatchCommand });
  const miniGalaxy = mountMiniGalaxy(document.body);
  const jpTooltip = mountJpTooltip(document.body);
  const researchPanel = mountResearchPanel(document.body, {
    queueCommand: dispatchCommand,
  });
  const devToolbox = mountDevToolbox(document.body, { uiState: ui });
  const fleetPanel = mountFleetPanel(document.body);

  // Start the sim loop. Initial speed dispatched by the controller below
  // — startSimLoop opts.initialSpeed=0 so we don't double-emit before
  // the controller takes over.
  const simLoop = startSimLoop(state, { initialSpeed: 0 });
  queueCommandRef = (cmd) => simLoop.queueCommand(cmd);

  // Speed controller (v2M3) — single source of truth for sim speed,
  // combining the player-driven user pause + speed ladder with the
  // overlay-driven menu pause. Effective speed flows through here to
  // simLoop.setSpeed. Initial speed seeded from settings.gameplay
  // .defaultGameSpeed (v2M4) so a player who set "default 4×" lands at
  // 4× when they start or load a game.
  const initialSpeed = getSettings
    ? getSettings().gameplay.defaultGameSpeed
    : 1;
  const speedController = createSpeedController({
    setSpeed: (effective) => simLoop.setSpeed(effective),
    initialSpeed,
  });
  const speedWidget = mountSpeedWidget(document.body, speedController);

  // In-game menu state. The menu is mounted on demand when Esc is pressed
  // with nothing to dismiss. While the menu is open, the sim loop is
  // paused via the speed controller's menu-pause path so the player can
  // make a decision without the world advancing.
  let inGameMenu: InGameMenuHandle | null = null;
  // v3M12.2 — end-game overlay handle. Mounted on outcome flip (the
  // render-loop poll below); held in scope so destroy() can tear it
  // down on session teardown. `endGameContinued` records that the
  // player clicked Continue Playing — keeps the overlay from
  // re-mounting on every frame after dismissal.
  let endGameOverlay: EndGameOverlayHandle | null = null;
  let endGameContinued = false;

  const openInGameMenu = (): void => {
    if (inGameMenu !== null) return;
    speedController.pauseForMenu();
    inGameMenu = mountInGameMenu(document.body, {
      onResume: closeInGameMenu,
      onSaveGame: onSave
        ? () => {
            // The save flow (slot picker) is owned by main.ts. It runs
            // while the menu remains open so the picker can layer on top;
            // closing here would unpause the sim mid-decision. The save
            // host is responsible for calling closeInGameMenu when it
            // finishes (cancel or confirm) — see main.ts onSave wiring.
            onSave(() => state);
          }
        : undefined,
      onSettings: () => {
        // Mount the Settings overlay on top of the menu. Owned by main.ts
        // because Settings is also reachable from the main menu and the
        // overlay shouldn't be re-implemented here.
        onSettings?.();
      },
      onReturnToMainMenu: () => {
        const shouldConfirm =
          getSettings?.().gameplay.confirmReturnToMainMenu ?? true;
        if (shouldConfirm && !confirm('Discard current game and return to main menu?')) {
          return;
        }
        // No un-pause; the session is about to be torn down.
        onReturnToMainMenu();
      },
      onConcede: () => {
        // v3M12.4 — manual surrender. Confirm gate is unconditional
        // (no Settings toggle in v1 — surrender is rarer + more
        // consequential than return-to-menu). Dispatches the concede
        // command which flips gameOutcome to 'defeat' on the next sim
        // tick before the freeze guard takes hold; the render loop
        // then mounts the end-game overlay automatically.
        if (!confirm('Concede the game? Your empire will be marked as defeated.')) {
          return;
        }
        dispatchCommand({ kind: 'concede' });
        closeInGameMenu();
      },
      onExit: () => {
        const shouldConfirm = getSettings?.().gameplay.confirmExit ?? true;
        if (shouldConfirm && !confirm('Exit Strategos Universalis?')) return;
        onExit();
      },
    });
  };

  const closeInGameMenu = (): void => {
    if (inGameMenu === null) return;
    inGameMenu.destroy();
    inGameMenu = null;
    speedController.resumeFromMenu();
  };

  const input = attachInputHandlers({
    canvas,
    uiState: ui,
    getGameState: () => state,
    getViewport: () => computeViewport(canvas, ui),
    queueCommand: dispatchCommand,
    onOpenMenu: openInGameMenu,
    speedController,
  });

  // rAF-driven render at display rate, independent of sim tick rate.
  //
  // The loop body is wrapped in try/catch so a bug in any one frame (e.g. a
  // renderer ReferenceError) logs to console instead of killing the rAF
  // loop and leaving the canvas permanently frozen.
  let renderRunning = true;
  let renderErrorLogged = false;
  const renderLoop = (): void => {
    if (!renderRunning) return;
    try {
      // v3M19.3 — fetch the floating-damage-numbers gameplay toggle
      // each frame so a Settings save mid-session takes effect on the
      // very next frame without a session restart. Defaults to `true`
      // when no getSettings was injected (test scaffolds, ad-hoc embed).
      const showDamageNumbers =
        getSettings?.().gameplay.showDamageNumbers ?? true;
      renderFrame(rc, state, ui, { showDamageNumbers });
      hud.update(state);
      speedWidget.update();
      panel.update(state, ui);
      miniGalaxy.update(state, ui);
      jpTooltip.update(state, ui);
      researchPanel.update(state);
      devToolbox.update(state);
      fleetPanel.update(state, ui);
      alertFeed.update(state, state.playerEmpireId);
      // v3M12.2 — mount the end-game overlay the first frame the
      // outcome flips. Re-mount-after-Continue is suppressed by
      // `endGameContinued`. Outcome flips are one-way; once mounted,
      // the overlay stays unless explicitly dismissed.
      if (
        endGameOverlay === null &&
        !endGameContinued &&
        state.gameOutcome !== 'in-progress'
      ) {
        // v3M12.3 — fire the audio stinger alongside the overlay
        // mount. Engine handles the 1 s ambient fade + 1 s acoustic
        // gap so the stinger lands cleanly even from a busy combat
        // moment.
        getAudioEngine().playEndGameStinger(
          state.gameOutcome === 'victory' ? 'music:victory' : 'music:defeat',
        );
        endGameOverlay = mountEndGameOverlay(
          document.body,
          state,
          state.gameOutcome,
          {
            onReturnToMainMenu: () => {
              if (endGameOverlay !== null) {
                endGameOverlay.destroy();
                endGameOverlay = null;
              }
              onReturnToMainMenu();
            },
            onContinue: () => {
              endGameContinued = true;
              if (endGameOverlay !== null) {
                endGameOverlay.destroy();
                endGameOverlay = null;
              }
            },
          },
        );
      }
    } catch (err) {
      // Log the first error loudly; keep looping so later frames can still
      // draw if the bug is transient. Subsequent errors are summarised to
      // avoid console spam.
      if (!renderErrorLogged) {
        console.error('Render loop error (loop will continue):', err);
        renderErrorLogged = true;
      } else {
        console.warn('Render loop error (suppressed)');
      }
    }
    requestAnimationFrame(renderLoop);
  };
  requestAnimationFrame(renderLoop);

  // Debug handles on window — removed in a later milestone. Session-scoped
  // so Return-to-Main-Menu drops stale references.
  Object.assign(window as unknown as Record<string, unknown>, {
    __state: state,
    __ui: ui,
  });

  console.info(
    `Strategos Universalis booted. ${state.systems.size} systems, ${state.empires.size} empires, ${state.ships.size} ships.`,
  );

  return {
    destroy(): void {
      renderRunning = false;
      simLoop.stop();
      // In-game menu may be open when the user clicked Return to Main Menu;
      // tear it down before the rest of the session so it doesn't survive
      // the transition as an orphan overlay.
      if (inGameMenu !== null) {
        inGameMenu.destroy();
        inGameMenu = null;
      }
      // v3M12.2 — same treatment for the end-game overlay if it's
      // still mounted (e.g. session teardown via the unload path
      // rather than the overlay's own Return-to-Main-Menu button).
      if (endGameOverlay !== null) {
        endGameOverlay.destroy();
        endGameOverlay = null;
      }
      input.destroy();
      researchPanel.destroy();
      devToolbox.destroy();
      fleetPanel.destroy();
      jpTooltip.destroy();
      miniGalaxy.destroy();
      panel.destroy();
      speedWidget.destroy();
      alertFeed.destroy();
      hud.destroy();
      // v2M8.7 — fade out the ambient bed (1 s ramp). Engine drops
      // its ambientBed handle synchronously so a fast Return-to-Menu
      // → New-Game round-trip doesn't double-instance.
      getAudioEngine().stopAmbientBed();
      // Clear debug globals so the old state can GC.
      const w = window as unknown as Record<string, unknown>;
      delete w.__state;
      delete w.__ui;
      // Repaint the canvas to a blank frame so the torn-down session doesn't
      // leave its last render visible under the main menu overlay.
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
    },
  };
}
